<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12">
        <!-- Header -->
        <v-row align="center" class="mt-n4">
          <v-col cols="12" md="auto">
            <div class="d-flex align-center">
              <v-btn icon class="mr-2" @click="goBack">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-avatar color="primary" size="48" class="mr-3 elevation-2">
                <v-icon color="white" size="26">mdi-account-group</v-icon>
              </v-avatar>
              <div>
                <div class="text-h5 font-weight-bold grey--text text--darken-2">
                  Grupo de liquidación #{{ benefit_group_id }}
                </div>
                <div class="text-caption grey--text">
                  {{ processedCount }} de {{ employees.length }} empleados procesados
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-card class="rounded-lg mt-2">
          <v-row v-if="loading" justify="center">
            <v-col cols="12" md="10">
              <v-skeleton-loader type="table"></v-skeleton-loader>
            </v-col>
          </v-row>
          <v-data-table v-else :headers="headers" :items="employees" :items-per-page="50"
            item-key="employee_id" show-select v-model="selected" :single-select="false" class="elevation-0" dense
            :item-class="rowClass">
            <template v-slot:top>
              <v-toolbar class="elevation-0 rounded-lg">
                <v-toolbar-title class="text-subtitle-1 font-weight-bold">
                  Empleados
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn color="primary" depressed class="text-none" :disabled="selectedPending.length == 0"
                  @click="processSelected">
                  <v-icon left>mdi-cog-play</v-icon>
                  Procesar seleccionados ({{ selectedPending.length }})
                </v-btn>
              </v-toolbar>
            </template>

            <!-- only pending rows are selectable -->
            <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
              <v-simple-checkbox v-if="!item.invoice_id" :value="isSelected" @input="select($event)"
                :ripple="false"></v-simple-checkbox>
              <v-icon v-else color="success" small>mdi-lock-check</v-icon>
            </template>

            <template v-slot:[`item.total_amount_of_accrual`]="{ item }">
              {{ currencyFormat(item.total_amount_of_accrual) }}
            </template>
            <template v-slot:[`item.total_amount_of_deduction`]="{ item }">
              {{ currencyFormat(item.total_amount_of_deduction) }}
            </template>
            <template v-slot:[`item.net_amount`]="{ item }">
              <span class="font-weight-bold">{{ currencyFormat(netAmount(item)) }} {{ item.currency_code }}</span>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip v-if="item.invoice_id" small color="success" outlined class="font-weight-bold">
                <v-icon left small>mdi-check-circle</v-icon>
                {{ item.invoice_number }}
              </v-chip>
              <v-chip v-else small color="warning" outlined class="font-weight-bold">
                <v-icon left small>mdi-clock-outline</v-icon>
                Pendiente
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn v-if="item.invoice_id" small text color="primary" class="text-none"
                @click="showInvoice(item.invoice_id, true)">
                <v-icon left small>mdi-file-eye</v-icon>
                Ver factura
              </v-btn>
              <v-btn v-else small outlined color="primary" class="text-none" @click="processOne(item)">
                <v-icon left small>mdi-cog-play</v-icon>
                Procesar
              </v-btn>
            </template>

            <template v-slot:no-data>
              <v-row justify="center" class="pt-10">
                <v-icon size="80px">mdi-account-off-outline</v-icon>
              </v-row>
              <v-row justify="center" class="pb-6"> No hay empleados en este grupo </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "BenefitGroupDetail",
  data() {
    return {
      loading: false,
      employees: [],
      selected: [],
      headers: [
        { text: "Localidad", value: "location_name", align: "start" },
        { text: "Empleado", value: "employee_fullname", align: "start" },
        { text: "Cédula", value: "dni", align: "start" },
        { text: "Devengos", value: "total_amount_of_accrual", align: "end" },
        { text: "Deducciones", value: "total_amount_of_deduction", align: "end" },
        { text: "Neto", value: "net_amount", align: "end" },
        { text: "Estado", value: "status", align: "center" },
        { text: "Acción", value: "actions", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    benefit_group_id() {
      return parseInt(this.$route.params.benefit_group_id);
    },
    processedCount() {
      return this.employees.filter((e) => e.invoice_id).length;
    },
    selectedPending() {
      return this.selected.filter((e) => !e.invoice_id);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    netAmount(item) {
      return (
        parseFloat(item.total_amount_of_accrual || 0) -
        parseFloat(item.total_amount_of_deduction || 0)
      );
    },
    rowClass(item) {
      return item.invoice_id ? "grey lighten-4" : "";
    },
    async getData() {
      this.loading = true;
      try {
        const response = await this.api_Post("humanResource/benefit_group_employees", {
          benefit_group_id: this.benefit_group_id,
        });
        const result = await response.json();
        this.employees = Array.isArray(result) ? result : [];
        this.selected = [];
      } catch (error) {
        console.error(error);
        this.$toast.error("Error al cargar los empleados del grupo");
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push({ name: "BenefitInvoices" });
    },
    processOne(item) {
      this.goToProcess([item.employee_id]);
    },
    processSelected() {
      const ids = this.selectedPending.map((e) => e.employee_id);
      if (ids.length == 0) return;
      this.goToProcess(ids);
    },
    goToProcess(employeeIds) {
      this.$router.push({
        name: "BenefitProcess",
        params: { benefit_group_id: this.benefit_group_id },
        query: { employee_ids: employeeIds.join(",") },
      });
    },
  },
};
</script>
